import {createContext, useContext, useEffect, useState} from 'react';
import {
    createUserWithEmailAndPassword,
    onAuthStateChanged,
    sendPasswordResetEmail,
    signInWithEmailAndPassword,
    signOut
} from 'firebase/auth';
import {get, ref, set} from 'firebase/database';
import {auth, database} from '../config/firebase';

const AuthContext = createContext();

export const AuthProvider = ({children}) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);

    // Vérifier le rôle de l'utilisateur
    const fetchUserRole = async (uid) => {
        try {
            const roleRef = ref(database, `roles/${uid}`);
            const snapshot = await get(roleRef);
            if (snapshot.exists()) {
                return snapshot.val();
            }
            return {role: 'user'};
        } catch (error) {
            console.error("Erreur lors de la récupération du rôle:", error);
            return {role: 'user'};
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);
            if (user) {
                const roleData = await fetchUserRole(user.uid);
                setUserRole(roleData);
            } else {
                setUserRole(null);
            }
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    // Connexion
    const login = (email, password) => {
        return signInWithEmailAndPassword(auth, email, password);
    };

    // Inscription
    const register = async (email, password, userData) => {
        try {
            // Vérification des doublons avant l'inscription
            const emailLookupRef = ref(database, `userLookup/byEmail/${email}`);
            const phoneLookupRef = ref(database, `userLookup/byPhone/${userData.telephone}`);

            const emailSnapshot = await get(emailLookupRef);
            const phoneSnapshot = await get(phoneLookupRef);

            if (emailSnapshot.exists()) {
                throw new Error("Cet email est déjà utilisé.");
            }

            if (phoneSnapshot.exists()) {
                throw new Error("Ce numéro de téléphone est déjà utilisé.");
            }

            // Création du compte
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const {uid} = userCredential.user;

            // Enregistrement des données utilisateur
            const userRef = ref(database, `users/${uid}/profile`);
            await set(userRef, {
                ...userData,
                createdAt: Date.now(),
                updatedAt: Date.now()
            });

            // Création des entrées dans les tables de référence
            await set(ref(database, `userLookup/byEmail/${email}`), uid);
            await set(ref(database, `userLookup/byPhone/${userData.telephone}`), uid);

            // Ajouter l'utilisateur à la liste des membres de l'église
            await set(ref(database, `churchMembers/${userData.churchId}/${uid}`), true);

            // Mettre à jour les statistiques de l'église
            const churchStatsRef = ref(database, `statistics/churches/${userData.churchId}`);
            const globalStatsRef = ref(database, `statistics/global`);

            const churchStatsSnapshot = await get(churchStatsRef);
            const globalStatsSnapshot = await get(globalStatsRef);

            if (churchStatsSnapshot.exists()) {
                const churchStats = churchStatsSnapshot.val();
                await set(churchStatsRef, {
                    ...churchStats,
                    totalMembres: (churchStats.totalMembres || 0) + 1,
                    hommes: userData.sexe === 'M' ? (churchStats.hommes || 0) + 1 : (churchStats.hommes || 0),
                    femmes: userData.sexe === 'F' ? (churchStats.femmes || 0) + 1 : (churchStats.femmes || 0),
                    baptises: userData.baptise ? (churchStats.baptises || 0) + 1 : (churchStats.baptises || 0)
                });
            } else {
                await set(churchStatsRef, {
                    totalMembres: 1,
                    hommes: userData.sexe === 'M' ? 1 : 0,
                    femmes: userData.sexe === 'F' ? 1 : 0,
                    baptises: userData.baptise ? 1 : 0
                });
            }

            if (globalStatsSnapshot.exists()) {
                const globalStats = globalStatsSnapshot.val();
                await set(globalStatsRef, {
                    ...globalStats,
                    totalMembres: (globalStats.totalMembres || 0) + 1,
                    hommes: userData.sexe === 'M' ? (globalStats.hommes || 0) + 1 : (globalStats.hommes || 0),
                    femmes: userData.sexe === 'F' ? (globalStats.femmes || 0) + 1 : (globalStats.femmes || 0),
                    baptises: userData.baptise ? (globalStats.baptises || 0) + 1 : (globalStats.baptises || 0)
                });
            } else {
                await set(globalStatsRef, {
                    totalMembres: 1,
                    hommes: userData.sexe === 'M' ? 1 : 0,
                    femmes: userData.sexe === 'F' ? 1 : 0,
                    baptises: userData.baptise ? 1 : 0
                });
            }

            // Mettre à jour les statistiques par profession
            const professionRef = ref(database, `statistics/professions/${userData.profession}`);
            const professionSnapshot = await get(professionRef);

            if (professionSnapshot.exists()) {
                await set(professionRef, professionSnapshot.val() + 1);
            } else {
                await set(professionRef, 1);
            }

            return userCredential;
        } catch (error) {
            console.error("Erreur lors de l'inscription:", error);
            throw error;
        }
    };

    // Déconnexion
    const logout = () => {
        return signOut(auth);
    };

    // Réinitialisation du mot de passe
    const resetPassword = (email) => {
        return sendPasswordResetEmail(auth, email);
    };

    const value = {
        currentUser,
        userRole,
        login,
        register,
        logout,
        resetPassword,
        loading
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};
